import type { FirebaseAllAvailabilitySlot } from "../../firebase/allAvailabilityTypes";

type NormalizedAvailability = FirebaseAllAvailabilitySlot[] | null;

const lastFingerprintByPath = new Map<string, string>();
const lastNormalizedByPath = new Map<string, NormalizedAvailability>();

/**
 * Availability identity contract (no dirty-date set):
 * 1. Fingerprint cache → same raw Firebase node keeps the same normalized array
 * 2. Live `onValue` skips batching when {@link isSameAvailabilitySnapshot}
 * 3. `merge` is the write barrier (also covers replay, which bypasses handlers)
 * 4. Discount derive skips restaurants whose `byId[id]` Immer subtree ref is unchanged
 *
 * Each onValue already names restaurant/session/date; unchanged paths never
 * replace store slots, so derive never needs a per-date dirty list.
 */
export const isSameAvailabilitySnapshot = (
  a: NormalizedAvailability | undefined,
  b: NormalizedAvailability | undefined,
): boolean => a === b;

/**
 * Order-independent fingerprint of the raw Firebase date-node object so we can
 * skip normalize when the payload is unchanged.
 *
 * Distinct sentinels for null vs empty-object (all falsy children) so those
 * states do not collide and return a stale cached normalize result.
 *
 * Includes walk-in fields. Backend writes the full slot row, but
 * `walkInDinersAvailable` can change after FT `dinersAvailable`/`available`
 * are already empty (TooLate inside a claim window). Omitting those fields
 * would reuse a stale normalized array and skip merge/derive.
 */
export function fingerprintFirebaseAllAvailabilityRaw(raw: unknown): string {
  if (raw == null) {
    return "\0null";
  }

  if (typeof raw !== "object") {
    return "\0invalid";
  }

  const parts: string[] = [];

  for (const value of Object.values(
    raw as Record<string, FirebaseAllAvailabilitySlot | null | undefined>,
  )) {
    if (!value) {
      continue;
    }

    const diners = value.dinersAvailable?.join(",") ?? "";
    const walkInDiners = value.walkInDinersAvailable?.join(",") ?? "";
    parts.push(
      [
        value.id,
        value.time ?? "",
        value.discount ?? "",
        diners,
        value.available ? "1" : "0",
        value.open ? "1" : "0",
        value.price ?? "",
        value.cutOffTime ?? "",
        value.walkInsEnabled ? "1" : "0",
        value.walkInClaimStart ?? "",
        value.walkInClaimEnd ?? "",
        walkInDiners,
        value.walkInDiscount ?? "",
      ].join("|"),
    );
  }

  if (parts.length === 0) {
    return "\0empty";
  }

  parts.sort();
  return parts.join(";");
}

/** Fingerprint hit → reuse last normalized payload (still call onValue for nested stores). */
export function getCachedNormalizedAvailability(
  path: string,
  rawFingerprint: string,
): NormalizedAvailability | undefined {
  if (lastFingerprintByPath.get(path) !== rawFingerprint) {
    return undefined;
  }

  if (!lastNormalizedByPath.has(path)) {
    return undefined;
  }

  return lastNormalizedByPath.get(path);
}

export function rememberNormalizedAvailability(
  path: string,
  rawFingerprint: string,
  normalized: NormalizedAvailability,
): void {
  lastFingerprintByPath.set(path, rawFingerprint);
  lastNormalizedByPath.set(path, normalized);
}

export function clearAvailabilitySnapshotCache(path?: string): void {
  if (path === undefined) {
    lastFingerprintByPath.clear();
    lastNormalizedByPath.clear();
    return;
  }

  lastFingerprintByPath.delete(path);
  lastNormalizedByPath.delete(path);
}
